import { useState, useEffect, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { Purchases, LOG_LEVEL } from '@revenuecat/purchases-capacitor';
import { RevenueCatUI, PAYWALL_RESULT } from '@revenuecat/purchases-capacitor-ui';
import { supabase } from '../lib/supabase';

const RC_API_KEY = 'goog_yLKStAZroPbpdctMxMfiohJmQGj';
const ENTITLEMENT_ID = 'CarTrack +'; // Must match exactly what's in the RevenueCat dashboard

/**
 * Converts a raw RevenueCat error into a user-friendly string.
 * Error 23 (CONFIG_ERROR / SDK already configured) is swallowed silently.
 * Numeric-only messages (raw codes) are replaced with a generic prompt.
 */
function humanizeRcError(e) {
    if (!e) return null;
    // Error 23 = already configured — never show to users
    if (e?.code === RC_CONFIG_ERROR_CODE) return null;
    const msg = e?.message || e?.toString() || '';
    // RC sometimes returns just the numeric code as the message
    if (/^\d+$/.test(msg.trim())) return 'Something went wrong. Please try again.';
    // Generic fallback for blank or unusable messages
    if (!msg || msg.length < 3) return 'Purchase failed. Please try again.';
    return msg;
}

// Module-level flags — survive component remounts so RC is never configured twice.
// A second Purchases.configure() call throws error 23 (CONFIG_ERROR).
let rcConfigured = false;
let prevUserId = undefined;

// Error code for RevenueCat CONFIG_ERROR (already configured)
const RC_CONFIG_ERROR_CODE = 23;

export function useRevenueCat(userId) {
    // Only trust the cached isPro flag when a user is actually logged in.
    // Without this guard, a logged-out session inherits the previous user's
    // pro status from localStorage and shows CarTrack+ without an account.
    const [isPro, setIsPro] = useState(() => {
        if (!userId) return false;
        return localStorage.getItem('isPro') === 'true';
    });
    // isProLoading: true while RC is still verifying the subscription on this device.
    // Consumers should not render premium-gated UI until this is false.
    const [isProLoading, setIsProLoading] = useState(true);
    // isRcReady: true once Purchases.configure() has successfully completed.
    // fetchOfferings and other RC calls must wait for this to be true.
    const [isRcReady, setIsRcReady] = useState(rcConfigured);
    const [offerings, setOfferings] = useState(null);
    const [serviceProducts, setServiceProducts] = useState(null);
    const [purchasing, setPurchasing] = useState(false);
    const [restoring, setRestoring] = useState(false);
    const [error, setError] = useState(null);
    const [offeringsError, setOfferingsError] = useState(null);


    // Sync the verified RC result to Supabase so the DB is always up-to-date.
    // This covers expiry (is_pro → false) and renewal (is_pro → true).
    // proSource='revenuecat' means it was set by an actual RC purchase/check.
    const syncProToSupabase = useCallback(async (hasPro) => {
        if (!userId) return; // Can't sync without a user
        try {
            await supabase
                .from('user_subscriptions')
                .upsert(
                    {
                        user_id: userId,
                        is_pro: hasPro,
                        pro_source: hasPro ? 'revenuecat' : null,
                        updated_at: new Date().toISOString(),
                    },
                    { onConflict: 'user_id' },
                );
        } catch (e) {
            console.warn('Failed to sync pro status to Supabase:', e);
        }
    }, [userId]);

    // Check if user has active "pro" entitlement
    const refreshProStatus = useCallback(async () => {
        try {
            // If pro was unlocked via a promo code, skip RevenueCat check —
            // RC has no record of a purchase so it would incorrectly reset isPro to false.
            // Guard: only honour the promo bypass when a real user is logged in.
            // Without this, a logged-out session inherits a prior user's promo unlock.
            if (userId && localStorage.getItem('proSource') === 'promo') {
                setIsPro(true);
                return true;
            }
            const { customerInfo } = await Purchases.getCustomerInfo();
            const active = customerInfo.entitlements.active;
            const hasPro = ENTITLEMENT_ID in active;
            setIsPro(hasPro);
            localStorage.setItem('isPro', hasPro ? 'true' : 'false');
            // Keep Supabase in sync — covers expiry (false) and renewal (true)
            await syncProToSupabase(hasPro);
            return hasPro;
        } catch (e) {
            console.warn('Failed to refresh pro status:', e);
            // Don't override a promo unlock on error — but only if a user is logged in
            if (userId && localStorage.getItem('proSource') === 'promo') return true;
            return false;
        }
    }, [userId, syncProToSupabase]);

    useEffect(() => {
        const platform = Capacitor.getPlatform();

        // RevenueCat only runs on native platforms
        if (platform !== 'ios' && platform !== 'android') {
            console.info('RevenueCat: skipping on web platform');
            // If the user signed out on web, userId becomes null, so we must reset isPro
            if (!userId) {
                setIsPro(false);
            } else {
                setIsPro(localStorage.getItem('isPro') === 'true');
            }
            setIsProLoading(false); // No RC check needed on web
            return;
        }

        const handleAuth = async () => {
            try {
                await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG });

                // ── First time: configure RC once ──
                // Check native SDK first (survives JS hot-reloads where rcConfigured resets)
                const alreadyConfigured = rcConfigured ||
                    (typeof Purchases.isConfigured === 'function' && (await Purchases.isConfigured().catch(() => false)));

                if (!alreadyConfigured) {
                    try {
                        await Purchases.configure({
                            apiKey: RC_API_KEY,
                            appUserID: userId || null, // null = anonymous on first launch
                        });
                    } catch (configErr) {
                        // Error 23 = SDK already configured (e.g. after hot reload).
                        // Treat it as success and continue — do NOT re-throw.
                        if (configErr?.code !== RC_CONFIG_ERROR_CODE) throw configErr;
                        console.info('RevenueCat: SDK was already configured (error 23 suppressed).');
                    }
                    rcConfigured = true;
                    setIsRcReady(true); // Signal that RC is configured and safe to call

                    // Load offerings once after first configure
                    try {
                        const { offerings: offeringsData } = await Purchases.getOfferings();
                        // Use current offering; if not set in RC dashboard fall back to
                        // the first available offering so packages always load on device.
                        const best =
                            offeringsData.current ??
                            Object.values(offeringsData.all ?? {})[0] ??
                            null;
                        if (best) {
                            setOfferings(best);
                            setOfferingsError(null);
                        } else {
                            const msg = 'No offerings found in RevenueCat dashboard. Make sure your app has an active Offering configured.';
                            console.warn('RevenueCat:', msg);
                            setOfferingsError(msg);
                        }

                        const sProducts = await Purchases.getProducts(['car_track_service:cartrackservice', 'car_track_service_yearly:serviceyearly']);
                        if (sProducts.length > 0) setServiceProducts(sProducts);
                    } catch (e) {
                        const msg = humanizeRcError(e) || e?.message || String(e);
                        console.warn('RevenueCat metadata fetch failed:', msg, e);
                        setOfferingsError(`Store error: ${msg}`);
                    }

                    await refreshProStatus();
                    prevUserId = userId;
                    setIsProLoading(false);
                    return;
                }

                // SDK was already configured from a previous mount
                rcConfigured = true;
                setIsRcReady(true);

                // ── userId didn't change — nothing to do ──
                if (userId === prevUserId) {
                    setIsProLoading(false);
                    return;
                }

                const wasLoggedIn = !!prevUserId;
                const isNowLoggedIn = !!userId;

                if (isNowLoggedIn) {
                    // User signed in (or switched account) — identify them in RC
                    // logIn() fetches their subscription from the RevenueCat backend
                    console.info('RevenueCat: logging in as', userId);
                    await Purchases.logIn({ appUserID: userId });
                    await refreshProStatus();
                } else if (wasLoggedIn && !isNowLoggedIn) {
                    // User signed out — reset RC to anonymous so pro status is cleared
                    console.info('RevenueCat: logging out');
                    await Purchases.logOut();
                    setIsPro(false);
                    localStorage.removeItem('isPro');
                }

                prevUserId = userId;
            } catch (e) {
                console.warn('RevenueCat auth change error:', e);
            } finally {
                setIsProLoading(false);
            }
        };

        handleAuth();
        // refreshProStatus is stable (useCallback with no deps), userId is the only real dep
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);

    // Purchase a package
    const purchasePackage = useCallback(async (rcPackage) => {
        setPurchasing(true);
        setError(null);
        try {
            const { customerInfo } = await Purchases.purchasePackage({ aPackage: rcPackage });
            const hasPro = ENTITLEMENT_ID in customerInfo.entitlements.active;
            setIsPro(hasPro);
            localStorage.setItem('isPro', hasPro ? 'true' : 'false');
            await syncProToSupabase(hasPro);
            return { success: hasPro };
        } catch (e) {
            if (e.code !== 'PURCHASE_CANCELLED') {
                const msg = humanizeRcError(e);
                if (msg) setError(msg);
            }
            return { success: false, cancelled: e.code === 'PURCHASE_CANCELLED' };
        } finally {
            setPurchasing(false);
        }
    }, [syncProToSupabase]);

    // Direct purchase for the Service Account Application Fee (Monthly or Yearly)
    const purchaseServiceAccount = useCallback(async (plan = 'monthly') => {
        setPurchasing(true);
        setError(null);
        try {
            const productId = plan === 'yearly' 
                ? 'car_track_service_yearly:serviceyearly' 
                : 'car_track_service:cartrackservice';
                
            // Provide the exact Google Play format product identifier
            const products = await Purchases.getProducts([productId]);
            if (!products || products.length === 0) {
                throw new Error('Service payment item not found in store.');
            }
            
            // Purchase the product directly
            await Purchases.purchaseStoreProduct({ product: products[0] });
            return { success: true };
        } catch (e) {
            if (e.code !== 'PURCHASE_CANCELLED') {
                const msg = humanizeRcError(e);
                if (msg) setError(msg);
            }
            return { success: false, cancelled: e.code === 'PURCHASE_CANCELLED' };
        } finally {
            setPurchasing(false);
        }
    }, []);

    // Restore previous purchases (required by App Store)
    const restorePurchases = useCallback(async () => {
        setRestoring(true);
        setError(null);
        try {
            const { customerInfo } = await Purchases.restorePurchases();
            const hasPro = ENTITLEMENT_ID in customerInfo.entitlements.active;
            setIsPro(hasPro);
            localStorage.setItem('isPro', hasPro ? 'true' : 'false');
            await syncProToSupabase(hasPro);
            return { restored: hasPro };
        } catch (e) {
            const msg = humanizeRcError(e);
            setError(msg || 'Restore failed. Please try again.');
            return { restored: false };
        } finally {
            setRestoring(false);
        }
    }, [syncProToSupabase]);

    // Present the native RevenueCat paywall UI
    // RC handles purchase + restore internally and returns a result
    const presentPaywall = useCallback(async () => {
        // RevenueCat paywall is native-only — bail silently on web
        if (Capacitor.getPlatform() === 'web') {
            console.info('RevenueCat: presentPaywall skipped on web platform');
            return false;
        }
        setPurchasing(true);
        setError(null);
        try {
            // Safety net: if the native paywall Promise never resolves (e.g. mid-update),
            // reset after 15 s so the button doesn't stay stuck in "Opening…" forever.
            const timeoutPromise = new Promise((_, reject) =>
                setTimeout(() => reject(new Error('Paywall timed out')), 15000)
            );
            const { result } = await Promise.race([
                RevenueCatUI.presentPaywall(),
                timeoutPromise,
            ]);
            switch (result) {
                case PAYWALL_RESULT.PURCHASED:
                case PAYWALL_RESULT.RESTORED:
                    await refreshProStatus();
                    return true;
                case PAYWALL_RESULT.NOT_PRESENTED:
                case PAYWALL_RESULT.ERROR:
                case PAYWALL_RESULT.CANCELLED:
                default:
                    return false;
            }
        } catch (e) {
            console.warn('Paywall error:', e);
            const msg = humanizeRcError(e);
            if (msg) setError(msg);
            return false;
        } finally {
            setPurchasing(false);
        }
    }, [refreshProStatus]);

    // Fetch offerings on-demand (e.g. when the payment modal opens)
    const fetchOfferings = useCallback(async () => {
        if (Capacitor.getPlatform() === 'web') return;
        // Guard: RC must be configured before calling getOfferings().
        // If not yet ready, wait up to 5 s for configure() to complete.
        if (!rcConfigured) {
            console.info('RevenueCat: fetchOfferings waiting for SDK to configure…');
            const deadline = Date.now() + 5000;
            await new Promise((resolve) => {
                const check = setInterval(() => {
                    if (rcConfigured || Date.now() > deadline) {
                        clearInterval(check);
                        resolve();
                    }
                }, 100);
            });
            if (!rcConfigured) {
                const msg = 'Store not ready yet. Please try again in a moment.';
                console.warn('RevenueCat: fetchOfferings timed out waiting for configure.');
                setOfferingsError(msg);
                return;
            }
        }
        try {
            const { offerings: offeringsData } = await Purchases.getOfferings();
            const best =
                offeringsData.current ??
                Object.values(offeringsData.all ?? {})[0] ??
                null;
            if (best) {
                setOfferings(best);
                setOfferingsError(null);
            } else {
                const msg = 'No offerings found. Check your RevenueCat dashboard — make sure an Offering is configured and active for Android.';
                console.warn('RevenueCat:', msg);
                setOfferingsError(msg);
            }
        } catch (e) {
            const msg = humanizeRcError(e) || e?.message || String(e);
            console.warn('fetchOfferings failed:', msg, e);
            setOfferingsError(`Store error: ${msg}`);
        }
    }, []);

    return {
        isPro,
        isProLoading,
        isRcReady,
        offerings,
        offeringsError,
        serviceProducts,
        purchasing,
        restoring,
        error,
        setError,
        setIsPro,
        purchasePackage,
        purchaseServiceAccount,
        restorePurchases,
        presentPaywall,
        fetchOfferings,
        refreshProStatus,
    };
}
